export const OVERFLOW_SCROLLBAR_MIN_THUMB_PX = 48;

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {{
 *   scrollHeight: number,
 *   clientHeight: number,
 *   scrollTop: number,
 *   trackHeight: number,
 *   minThumbHeight?: number,
 * }} input
 * @returns {{
 *   canScroll: boolean,
 *   thumbHeight: number,
 *   thumbOffset: number,
 *   maxScrollTop: number,
 * }}
 */
export function computeOverflowScrollbarMetrics({
  scrollHeight,
  clientHeight,
  scrollTop,
  trackHeight,
  minThumbHeight = OVERFLOW_SCROLLBAR_MIN_THUMB_PX,
}) {
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  const canScroll = maxScrollTop > 1 && trackHeight > 0 && clientHeight > 0;

  if (!canScroll) {
    return {
      canScroll: false,
      thumbHeight: 0,
      thumbOffset: 0,
      maxScrollTop: 0,
    };
  }

  const proportionalHeight = (clientHeight / scrollHeight) * trackHeight;
  const thumbHeight = clampNumber(
    proportionalHeight,
    Math.min(minThumbHeight, trackHeight),
    trackHeight,
  );
  const maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
  const ratio = maxScrollTop === 0 ? 0 : clampNumber(scrollTop, 0, maxScrollTop) / maxScrollTop;

  return {
    canScroll: true,
    thumbHeight,
    thumbOffset: ratio * maxThumbOffset,
    maxScrollTop,
  };
}

/**
 * @param {{
 *   startScrollTop: number,
 *   deltaY: number,
 *   trackHeight: number,
 *   thumbHeight: number,
 *   maxScrollTop: number,
 * }} input
 * @returns {number}
 */
export function scrollTopFromThumbDelta({
  startScrollTop,
  deltaY,
  trackHeight,
  thumbHeight,
  maxScrollTop,
}) {
  const maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
  if (maxThumbOffset === 0) {
    return clampNumber(startScrollTop, 0, maxScrollTop);
  }
  return clampNumber(
    startScrollTop + (deltaY / maxThumbOffset) * maxScrollTop,
    0,
    maxScrollTop,
  );
}

/**
 * @param {{
 *   pointerY: number,
 *   trackTop: number,
 *   trackHeight: number,
 *   thumbHeight: number,
 *   maxScrollTop: number,
 * }} input
 * @returns {number}
 */
export function scrollTopFromTrackPointer({
  pointerY,
  trackTop,
  trackHeight,
  thumbHeight,
  maxScrollTop,
}) {
  const maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
  if (maxThumbOffset === 0) {
    return 0;
  }
  const thumbOffset = clampNumber(pointerY - trackTop - thumbHeight / 2, 0, maxThumbOffset);
  return (thumbOffset / maxThumbOffset) * maxScrollTop;
}
